import type { PropsWithChildren } from 'react';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Logger from '../../utils/Logger';

export enum LayoutSize {
  mobile = 'mobile',
  tablet = 'tablet',
  desktop = 'desktop',
}

export const LAYOUT_MIN_MAX_WIDTHS: Record<LayoutSize, [number, number]> = {
  [LayoutSize.mobile]: [0, 655],
  [LayoutSize.tablet]: [655, 916],
  [LayoutSize.desktop]: [916, Number.MAX_SAFE_INTEGER],
};

export function isBelowLayoutSize(
  currentSize: LayoutSize,
  targetSize: LayoutSize,
) {
  return (
    LAYOUT_MIN_MAX_WIDTHS[currentSize][0] < LAYOUT_MIN_MAX_WIDTHS[targetSize][0]
  );
}

export function responsiveLayoutSize() {
  const width = window?.innerWidth;

  if (width === undefined) return null;

  return Object.entries(LAYOUT_MIN_MAX_WIDTHS).find(
    ([, [min, max]]) => width >= min && width < max,
  )?.[0] as LayoutSize | null;
}

// eslint-disable-next-line import/no-unused-modules --- expose for testing
export const LayoutSizeContext = createContext<{
  layoutSize: LayoutSize | null;
} | null>(null);

export const LayoutSizeProvider = ({ children }: PropsWithChildren) => {
  const [currentSize, setCurrentSize] = useState(() => responsiveLayoutSize());

  useEffect(() => {
    const handleResize = () => {
      setCurrentSize(responsiveLayoutSize());
    };

    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const contextValue = useMemo(
    () => ({ layoutSize: currentSize }),
    [currentSize],
  );

  return (
    <LayoutSizeContext.Provider value={contextValue}>
      {children}
    </LayoutSizeContext.Provider>
  );
};

function useLayoutSize() {
  const value = useContext(LayoutSizeContext);
  const hasContext = !!value;
  useEffect(() => {
    if (!hasContext) {
      Logger.error('useLayoutSize must be used within a LayoutSizeProvider');
    }
  }, [hasContext]);

  return value?.layoutSize ?? null;
}

export default useLayoutSize;
